/**
 * Best-effort "is this a phone/tablet" check for gating desktop-only parts of
 * the respondent journey (a clickable Figma prototype, eye-tracking
 * calibration) before the respondent burns a slot on a device the task can't
 * meaningfully run on — calibrating gaze on a handheld phone screen is not
 * useful data.
 *
 * No single signal is reliable alone, so this stacks a few, from most to
 * least direct:
 * - `navigator.userAgentData.mobile` (UA-CH) is authoritative where Chromium
 *   exposes it, but Safari/Firefox don't implement it at all.
 * - The classic UA string still catches the large majority of Android/iOS
 *   phones and tablets.
 * - Modern iPadOS (13+) deliberately spoofs a desktop Mac UA to get the
 *   desktop web, so it never matches the regex above — caught separately via
 *   multi-touch on a "Macintosh" UA, which a real Mac never has.
 * - Last resort (older/unusual browsers, spoofed UAs): a coarse (touch)
 *   pointer combined with a phone-sized viewport.
 */
export function isMobileDevice(): boolean {
  type NavigatorUAData = { mobile?: boolean }
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData
  if (uaData && typeof uaData.mobile === 'boolean') return uaData.mobile

  const ua = navigator.userAgent || ''
  if (/Android|iPhone|iPad|iPod/.test(ua)) return true

  // iPadOS 13+ reports as "Macintosh" but exposes multi-touch, unlike a real Mac.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true

  try {
    const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false
    const smallScreen = Math.min(window.innerWidth, window.innerHeight) <= 820
    if (coarsePointer && smallScreen) return true
  } catch {
    /* matchMedia unsupported — ignore, fall through to false */
  }

  return false
}
